import { Component, computed, inject, input } from '@angular/core';
import { ILpActivity } from '@gametailors/leerplus';
import { LpRidLeeslijstActivity } from '../../../../typings/schema';
import { RidReadingMatrixNoTime } from '../../../mechanics/rid-reading-matrix-no-time/rid-reading-matrix-no-time';
import { RidReadingMatrixStopwatch } from '../../../mechanics/rid-reading-matrix-stopwatch/rid-reading-matrix-stopwatch';
import { RidReadingMatrixTimer } from '../../../mechanics/rid-reading-matrix-timer/rid-reading-matrix-timer';
import { RidAssignmentBackground } from '../../components/rid-assignment-background/rid-assignment-background';
import { LEERPLUS } from '../../rid-game.utils';
import { RidAnswerService } from '../../services/rid-answer.service';
import { leeslijstAnswers } from './rid-leeslijst-activity.utils';

@Component({
  selector: 'rid-leeslijst-activity',
  imports: [RidReadingMatrixNoTime, RidReadingMatrixStopwatch, RidReadingMatrixTimer, RidAssignmentBackground],
  templateUrl: './rid-leeslijst-activity.html',
  providers: [RidAnswerService],
})
export class RidLeeslijstActivity implements ILpActivity<LpRidLeeslijstActivity> {
  private readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $ridAnswerService = inject(RidAnswerService);

  activity = input.required<LpRidLeeslijstActivity>();

  matrices = computed(() => [this.activity().content.mode.words.map((group) => group.words.map((w) => w.woord))]);

  previousStopwatchDuration = computed(() => {
    const previousDurationMs = this.$lpApiService.state().mechanics.leeslijst_stopwatch.bestTime;
    return previousDurationMs === null ? undefined : Math.round(previousDurationMs / 1000);
  });
  previousStopwatchAccuracy = computed(() => {
    const previousAccuracy = this.$lpApiService.state().mechanics.leeslijst_stopwatch.bestAccuracy;
    return previousAccuracy === null ? undefined : previousAccuracy;
  });

  finishStopwatch(duration: number | undefined, accuracy: number | undefined) {
    if (duration !== undefined || accuracy !== undefined) {
      this.$lpApiService.state.update((s) => ({
        ...s,
        mechanics: {
          ...s.mechanics,
          leeslijst_stopwatch: {
            bestTime:
              duration === undefined
                ? (s.mechanics.leeslijst_stopwatch?.bestTime ?? null)
                : Math.min(s.mechanics.leeslijst_stopwatch?.bestTime ?? Infinity, duration),
            bestAccuracy:
              accuracy === undefined
                ? (s.mechanics.leeslijst_stopwatch?.bestAccuracy ?? null)
                : Math.max(s.mechanics.leeslijst_stopwatch?.bestAccuracy ?? -Infinity, accuracy),
          },
        },
      }));
    }

    this.reportReading(accuracy, duration);
    this.finish();
  }

  finishTimer(
    accuracy: number | undefined,
    selection: [matrix: number, col: number, row: number] | undefined,
    durationMs: number | undefined,
  ) {
    // Without a selection there's no telling which words were read
    if (selection) {
      this.reportReading(accuracy, durationMs, selection);
    }
    this.finish();
  }

  finishReading(accuracy: number | undefined) {
    this.reportReading(accuracy, undefined);
    this.finish();
  }

  /** Reports the words read, up to `lastRead` if given, with the rating and the speed over `durationMs` if known. */
  private reportReading(
    accuracy: number | undefined,
    durationMs: number | undefined,
    lastRead?: [matrix: number, col: number, row: number],
  ) {
    const answers = leeslijstAnswers([this.activity().content.mode.words], accuracy, durationMs, lastRead);
    this.$ridAnswerService.report(answers, this.activity().uid);
  }

  finish() {
    this.$lpApiService.finishCurrentActivity();
  }
}
